import { createFileRoute, Link } from "@tanstack/react-router";
import { Activity, AlertTriangle, Boxes, Cpu, Gauge, ListChecks, RefreshCcw, ShieldCheck, Sparkles, Zap } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { apiRequest } from "@/lib/api";
import {
  COMPUTE_CRM_API,
  normalizeCRMResponse,
  type ComputeCRMResponse,
  type CRMAction,
  type CRMAlert,
  type CRMAlertStatus,
  type CRMConnectionStatus,
  type CRMRecommendation,
  type CRMRecommendationPriority,
  type CRMResource,
  type CRMResourceStatus,
  type CRMSyncDiagnostic,
  type CRMWorkload,
} from "@/lib/compute-crm";
import { assignHardwareCostBands, hardwareCostBandLabel } from "@/lib/hardware-cost-band";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/compute-resource-management")({
  head: () => ({ meta: [{ title: "CRM · GENRL Wallet" }] }),
  component: ComputeResourceManagementPage,
});

type CRMTab = "overview" | "resources" | "workloads" | "optimise" | "alerts" | "actions";

const CRM_TABS: { id: CRMTab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "resources", label: "Resources" },
  { id: "workloads", label: "Workloads" },
  { id: "optimise", label: "Optimise" },
  { id: "alerts", label: "Alerts" },
  { id: "actions", label: "Actions" },
];

export function ComputeResourceManagementPage() {
  const [crm, setCrm] = useState<ComputeCRMResponse>(() => normalizeCRMResponse(null));
  const [activeTab, setActiveTab] = useState<CRMTab>("overview");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");

  const refresh = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      setLoadError("");
      const payload = await apiRequest<ComputeCRMResponse>(COMPUTE_CRM_API, { timeoutMs: 30000 });
      setCrm(normalizeCRMResponse(payload));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not load compute CRM.";
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const updateAlertStatus = async (id: string, status: CRMAlertStatus) => {
    try {
      await apiRequest(COMPUTE_CRM_API, {
        method: "PATCH",
        body: { id, status },
        timeoutMs: 15000,
      });
      await refresh(true);
      toast.success(status === "open" ? "Alert reopened" : `Alert ${status}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update alert.");
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const topProviders = useMemo(() => {
    return [...crm.connections].sort((a, b) => a.providerName.localeCompare(b.providerName));
  }, [crm.connections]);

  return (
    <main className="wallet-ops-shell flex-1">
      <div className="wallet-ops-container space-y-6">
        <header className="wallet-ops-topbar">
          <div className="wallet-ops-brand">
            <div className="wallet-ops-brand-box">
              <Cpu className="h-4 w-4" />
            </div>
            <div>
              <p className="wallet-ops-label !text-[var(--wallet-ops-fg)]">Manage</p>
              <p className="wallet-ops-subtle text-[11px] uppercase tracking-[0.1em]">Health, spend, and recommended actions</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void refresh(true)}
            disabled={refreshing}
            className="wallet-ops-icon-button"
            aria-label="Refresh compute resource management"
            title="Refresh compute resource management"
          >
            <RefreshCcw className={cn("h-4 w-4", refreshing && "animate-spin")} />
          </button>
        </header>

        <section className="wallet-ops-grid-frame lg:grid-cols-5">
          <MetricCard icon={ShieldCheck} label="Providers" value={`${formatNumber(crm.portfolio.healthyConnections)} / ${formatNumber(crm.portfolio.providerConnections)}`} hint="Healthy connections" />
          <MetricCard icon={Boxes} label="Resources" value={formatNumber(crm.portfolio.resources)} hint={`${formatNumber(crm.portfolio.runningResources)} running`} />
          <MetricCard icon={Activity} label="Workloads" value={formatNumber(crm.portfolio.workloads)} hint={`${formatNumber(crm.portfolio.activeWorkloads)} active`} />
          <MetricCard icon={Gauge} label="GPU hours" value={formatNumber(crm.portfolio.committedGpuHours)} hint={`${formatNumber(crm.portfolio.committedGpuCount)} GPUs committed`} />
          <MetricCard icon={Activity} label="Metric feeds" value={formatNumber(crm.portfolio.metricEnabledResources)} hint={`${formatNumber(crm.portfolio.unmeteredRunningResources)} running unmetered`} />
        </section>

        <nav className="wallet-ops-panel flex flex-wrap gap-2 p-2" aria-label="Compute CRM sections">
          {CRM_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "min-h-[38px] border px-4 text-[11px] font-medium uppercase tracking-[0.1em] transition",
                activeTab === tab.id
                  ? "border-[var(--wallet-ops-fg)] bg-[var(--wallet-ops-fg)] text-[var(--wallet-ops-bg)]"
                  : "border-transparent text-[var(--wallet-ops-muted)] hover:border-[var(--wallet-ops-border-strong)] hover:text-[var(--wallet-ops-fg)]",
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {activeTab === "overview" && (
          <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="wallet-ops-panel">
              <PanelHeader
                icon={Cpu}
                label="Provider network"
                hint="The compute providers GENRL can route through."
                action={<Link to="/store" className="wallet-ops-button !min-h-0 px-3 py-1.5 text-[10px]">Store</Link>}
              />
              {topProviders.length === 0 ? (
                <EmptyState loading={loading} text="No provider connections are available yet." />
              ) : (
                <div className="grid gap-px bg-[var(--wallet-ops-border)] md:grid-cols-2">
                  {topProviders.map((connection) => (
                    <div key={connection.id} className="bg-[var(--wallet-ops-surface)] p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-[15px] font-medium text-[var(--wallet-ops-fg)]">{connection.displayName}</p>
                          <p className="mt-1 text-[11px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">{connection.permissionMode}</p>
                        </div>
                        <StatusPill status={connection.status} />
                      </div>
                      <p className="mt-4 text-[12px] text-[var(--wallet-ops-subtle)]">
                        Last healthy sync: {connection.lastSuccessAt ? formatDateTime(connection.lastSuccessAt) : "not recorded"}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="wallet-ops-panel">
              <PanelHeader icon={Sparkles} label="Recommendations" hint="Simple next steps from GENRL." />
              <div className="grid gap-px bg-[var(--wallet-ops-border)]">
                <SignalRow label="Open recommendations" value={crm.recommendations.length} />
                <SignalRow label="Active alerts" value={crm.alerts.length} />
                <SignalRow label="Action ledger" value={crm.actions.length} />
                <SignalRow label="Failed syncs" value={crm.syncDiagnostics.filter((item) => item.status === "failed").length} />
                <SignalRow label="Generated" value={crm.generatedAt ? formatDateTime(crm.generatedAt) : "pending"} />
              </div>
            </div>
          </section>
        )}

        {activeTab === "resources" && (
          <TablePanel icon={Boxes} label="Resources" hint="Provider-side pods, VMs, instances, disks, volumes and file systems normalised into one table.">
            {loadError && <InlineError message={loadError} />}
            <SyncDiagnostics diagnostics={crm.syncDiagnostics} />
            {crm.resources.length > 0
              ? <ResourceTable resources={crm.resources} />
              : <EmptyState loading={loading} text={crm.syncDiagnostics.length > 0 ? "Provider sync completed, but no resources were returned." : "No synced resources yet. Refresh provider data to populate this table."} />}
          </TablePanel>
        )}

        {activeTab === "workloads" && (
          <TablePanel icon={Activity} label="Workloads" hint="Training, inference, notebooks and compute jobs inferred from provider resources.">
            {crm.workloads.length > 0
              ? <WorkloadTable workloads={crm.workloads} />
              : <EmptyState loading={loading} text="No workloads are currently tracked." />}
          </TablePanel>
        )}

        {activeTab === "optimise" && (
          <section className="wallet-ops-panel">
            <PanelHeader icon={Zap} label="Optimise" hint="Backend-generated recommendations for cost, launchability and provider health." />
            {crm.recommendations.length === 0 ? (
              <EmptyState loading={loading} text="No recommendations are open." />
            ) : (
              <div className="grid gap-px bg-[var(--wallet-ops-border)] lg:grid-cols-2">
                {crm.recommendations.map((recommendation) => (
                  <RecommendationCard key={recommendation.id} recommendation={recommendation} />
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === "alerts" && (
          <section className="wallet-ops-panel">
            <PanelHeader icon={AlertTriangle} label="Alerts" hint="Low balance, failed sync, idle resource and launchability risk signals." />
            {crm.alerts.length === 0 ? (
              <EmptyState loading={loading} text="No active alerts." />
            ) : (
              <div className="divide-y divide-[var(--wallet-ops-border)]">
                {crm.alerts.map((alert) => (
                  <AlertRow
                    key={alert.id}
                    alert={alert}
                    onUpdateStatus={(status) => void updateAlertStatus(alert.id, status)}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === "actions" && (
          <section className="wallet-ops-panel">
            <PanelHeader icon={ListChecks} label="Actions" hint="Audited CRM action requests. High-risk provider actions should still require preview and approval." />
            {crm.actions.length === 0 ? (
              <EmptyState loading={loading} text="No CRM actions have been requested yet." />
            ) : (
              <div className="divide-y divide-[var(--wallet-ops-border)]">
                {crm.actions.map((action) => <ActionRow key={action.id} action={action} />)}
              </div>
            )}
          </section>
        )}
      </div>
    </main>
  );
}

function ResourceTable({ resources }: { resources: CRMResource[] }) {
  if (resources.length === 0) return null;
  const costBands = assignHardwareCostBands(resources, (resource) => resource.hourlyPriceUsd);
  return (
    <DataTable columns={["Provider", "Resource", "Used for", "GPU", "Utilisation", "Storage", "Status", "Relative cost", "Runtime"]}>
      {resources.map((resource, index) => (
        <tr key={resource.id} className="text-[var(--wallet-ops-fg)]">
          <td className="px-4 py-3">{resource.providerName}</td>
          <td className="px-4 py-3">
            <p className="font-medium">{resource.name}</p>
            <p className="mt-1 font-mono text-[10px] text-[var(--wallet-ops-muted)]">{resource.resourceType} · {resource.providerResourceId}</p>
          </td>
          <td className="px-4 py-3 capitalize">{resource.purpose || resource.resourceType}</td>
          <td className="px-4 py-3">{resource.gpuType || "unknown"}{resource.gpuCount ? ` x${resource.gpuCount}` : ""}</td>
          <td className="px-4 py-3">
            <p>{formatUtilization(resource)}</p>
            <p className="mt-1 font-mono text-[10px] text-[var(--wallet-ops-muted)]">{resource.metricSource || "metric pending"}</p>
          </td>
          <td className="px-4 py-3">
            <p>{formatStorage(resource)}</p>
            <p className="mt-1 font-mono text-[10px] text-[var(--wallet-ops-muted)]">{resource.storageMountPath || resource.storageId || resource.region || "no mount"}</p>
          </td>
          <td className="px-4 py-3"><ResourceStatusPill status={resource.status} /></td>
          <td className="px-4 py-3">{hardwareCostBandLabel(costBands[index] || "Mid cost")}</td>
          <td className="px-4 py-3">{resource.runtimeUrl ? <a className="underline" href={resource.runtimeUrl} target="_blank" rel="noreferrer">Open</a> : "not verified"}</td>
        </tr>
      ))}
    </DataTable>
  );
}

function SyncDiagnostics({ diagnostics }: { diagnostics: CRMSyncDiagnostic[] }) {
  if (diagnostics.length === 0) return null;
  return (
    <div className="grid gap-px border-b border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-border)] md:grid-cols-2 xl:grid-cols-3">
      {diagnostics.map((item) => (
        <div key={item.id} className="bg-[var(--wallet-ops-surface)] p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[13px] font-medium text-[var(--wallet-ops-fg)]">{item.providerName}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">
                {item.status === "success" ? `${formatNumber(item.resourceCount)} resources` : "sync failed"}
              </p>
            </div>
            <span className={cn(
              "border px-2 py-1 text-[10px] uppercase tracking-[0.08em]",
              item.status === "success"
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700"
                : "border-red-500/40 bg-red-500/10 text-red-700",
            )}>
              {item.status}
            </span>
          </div>
          {item.error && <p className="mt-3 text-[12px] leading-5 text-red-700">{item.error}</p>}
          <p className="mt-3 text-[10px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">{formatDateTime(item.checkedAt)}</p>
        </div>
      ))}
    </div>
  );
}

function WorkloadTable({ workloads }: { workloads: CRMWorkload[] }) {
  if (workloads.length === 0) return null;
  return (
    <DataTable columns={["Workload", "Provider", "Used for", "Utilisation", "Runtime", "Cost so far", "Status", "Owner"]}>
      {workloads.map((workload) => (
        <tr key={workload.id} className="text-[var(--wallet-ops-fg)]">
          <td className="px-4 py-3">{workload.name}</td>
          <td className="px-4 py-3 uppercase">{workload.providerSlug}</td>
          <td className="px-4 py-3 capitalize">{workload.purpose || workload.workloadType}</td>
          <td className="px-4 py-3">
            <p>{formatWorkloadUtilization(workload)}</p>
            <p className="mt-1 font-mono text-[10px] text-[var(--wallet-ops-muted)]">{workload.metricSource || "metric pending"}</p>
          </td>
          <td className="px-4 py-3">{formatDuration(workload.runtimeSeconds)}</td>
          <td className="px-4 py-3">{workload.costSoFarUsd == null ? "pending" : formatCurrency(workload.costSoFarUsd)}</td>
          <td className="px-4 py-3"><ResourceStatusPill status={workload.status} /></td>
          <td className="px-4 py-3">{workload.owner}</td>
        </tr>
      ))}
    </DataTable>
  );
}

function RecommendationCard({ recommendation }: { recommendation: CRMRecommendation }) {
  return (
    <article className="bg-[var(--wallet-ops-surface)] p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className={cn("inline-flex border px-2 py-1 text-[10px] uppercase tracking-[0.08em]", priorityClass(recommendation.priority))}>
            {recommendation.priority}
          </span>
          <h3 className="mt-3 text-[16px] font-medium text-[var(--wallet-ops-fg)]">{recommendation.title}</h3>
        </div>
        <span className="font-mono text-[11px] uppercase text-[var(--wallet-ops-muted)]">{recommendation.type.replace(/_/g, " ")}</span>
      </div>
      <p className="mt-3 text-[13px] leading-6 text-[var(--wallet-ops-subtle)]">{recommendation.description}</p>
      <div className="mt-5 flex items-center justify-between border-t border-[var(--wallet-ops-border)] pt-4">
        <p className="text-[12px] text-[var(--wallet-ops-muted)]">
          Estimated saving: {recommendation.estimatedSavingUsdMonth == null ? "pending" : `${formatCurrency(recommendation.estimatedSavingUsdMonth)} / month`}
        </p>
        <button type="button" className="wallet-ops-button !min-h-0 px-3 py-1.5 text-[10px]" disabled>
          Preview action
        </button>
      </div>
    </article>
  );
}

function AlertRow({
  alert,
  onUpdateStatus,
}: {
  alert: CRMAlert;
  onUpdateStatus: (status: CRMAlertStatus) => void;
}) {
  return (
    <div className="grid gap-3 p-4 md:grid-cols-[130px_minmax(0,1fr)_220px] md:items-center">
      <div className="space-y-2">
        <span className={cn("inline-flex w-fit border px-2 py-1 text-[10px] uppercase tracking-[0.08em]", priorityClass(alert.severity))}>{alert.severity}</span>
        <span className="block w-fit border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-2 py-1 text-[10px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">
          {alert.status}
        </span>
      </div>
      <div>
        <p className="text-[14px] font-medium text-[var(--wallet-ops-fg)]">{alert.title}</p>
        <p className="mt-1 text-[12px] text-[var(--wallet-ops-subtle)]">{alert.message}</p>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">
          {alert.ruleId || alert.type.replace(/_/g, " ")}
        </p>
      </div>
      <div className="space-y-2 md:text-right">
        <p className="text-[11px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">{formatDateTime(alert.updatedAt || alert.createdAt)}</p>
        <div className="flex flex-wrap gap-2 md:justify-end">
          {alert.status === "open" && (
            <button type="button" onClick={() => onUpdateStatus("acknowledged")} className="wallet-ops-button !min-h-0 px-3 py-1.5 text-[10px]">
              Acknowledge
            </button>
          )}
          {alert.status !== "dismissed" && (
            <button type="button" onClick={() => onUpdateStatus("dismissed")} className="wallet-ops-button !min-h-0 px-3 py-1.5 text-[10px]">
              Dismiss
            </button>
          )}
          {alert.status !== "open" && (
            <button type="button" onClick={() => onUpdateStatus("open")} className="wallet-ops-button !min-h-0 px-3 py-1.5 text-[10px]">
              Reopen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionRow({ action }: { action: CRMAction }) {
  return (
    <div className="grid gap-3 p-4 md:grid-cols-[minmax(0,1fr)_130px_190px] md:items-center">
      <div>
        <p className="text-[14px] font-medium text-[var(--wallet-ops-fg)]">{action.actionType.replace(/_/g, " ")}</p>
        <p className="mt-1 text-[12px] text-[var(--wallet-ops-subtle)]">Requested by {action.requestedByEmail || "workspace user"}</p>
      </div>
      <span className="inline-flex w-fit border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-2 py-1 text-[10px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">{action.status}</span>
      <p className="text-[11px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">{formatDateTime(action.createdAt)}</p>
    </div>
  );
}

function TablePanel({
  icon,
  label,
  hint,
  children,
}: {
  icon: typeof Cpu;
  label: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <section className="wallet-ops-panel">
      <PanelHeader icon={icon} label={label} hint={hint} />
      {children}
    </section>
  );
}

function InlineError({ message }: { message: string }) {
  return (
    <div className="border-b border-red-500/40 bg-red-500/10 px-5 py-4 text-[13px] leading-5 text-red-700">
      {message}
    </div>
  );
}

function PanelHeader({
  icon: Icon,
  label,
  hint,
  action,
}: {
  icon: typeof Cpu;
  label: string;
  hint: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="wallet-ops-panel-header">
      <div className="wallet-ops-brand">
        <div className="wallet-ops-brand-box">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="wallet-ops-label">{label}</p>
          <p className="mt-1 text-[13px] text-[var(--wallet-ops-subtle)]">{hint}</p>
        </div>
      </div>
      {action}
    </div>
  );
}

function DataTable({ columns, children }: { columns: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1080px] text-left text-[12px]">
        <thead className="border-b border-[var(--wallet-ops-border)] text-[10px] uppercase tracking-[0.12em] text-[var(--wallet-ops-muted)]">
          <tr>{columns.map((column) => <th key={column} className="px-4 py-3 font-semibold">{column}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-[var(--wallet-ops-border)]">{children}</tbody>
      </table>
    </div>
  );
}

function SignalRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 bg-[var(--wallet-ops-surface)] p-4">
      <span className="wallet-ops-label">{label}</span>
      <span className="font-mono text-[13px] text-[var(--wallet-ops-fg)]">{value}</span>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, hint }: { icon: typeof Boxes; label: string; value: string; hint: string }) {
  return (
    <div className="grid min-h-[112px] grid-cols-[38px_1fr] gap-3 bg-[var(--wallet-ops-surface)] p-4">
      <div className="wallet-ops-metric-icon">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="wallet-ops-label">{label}</p>
        <p className="mt-2 text-[24px] font-medium leading-none text-[var(--wallet-ops-fg)]">{value}</p>
        <p className="mt-2 text-[12px] text-[var(--wallet-ops-subtle)]">{hint}</p>
      </div>
    </div>
  );
}

function EmptyState({ loading, text }: { loading: boolean; text: string }) {
  return (
    <div className="px-5 py-10 text-center text-[13px] text-[var(--wallet-ops-muted)]">
      {loading ? "Loading compute CRM..." : text}
    </div>
  );
}

function StatusPill({ status }: { status: CRMConnectionStatus }) {
  return (
    <span className={cn("inline-flex border px-2 py-1 text-[10px] uppercase tracking-[0.08em]", status === "healthy" ? "border-[var(--success)]/40 bg-[var(--success)]/10 text-[var(--success)]" : "border-[var(--warning)]/40 bg-[var(--warning)]/10 text-[var(--warning)]")}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

function ResourceStatusPill({ status }: { status: CRMResourceStatus }) {
  return (
    <span className={cn("inline-flex border px-2 py-1 text-[10px] uppercase tracking-[0.08em]", resourceStatusClass(status))}>
      {status}
    </span>
  );
}

function resourceStatusClass(status: CRMResourceStatus) {
  switch (status) {
    case "running":
      return "border-[var(--success)]/40 bg-[var(--success)]/10 text-[var(--success)]";
    case "launching":
      return "border-primary/35 bg-primary/10 text-primary";
    case "failed":
      return "border-[var(--destructive)]/40 bg-[var(--destructive)]/10 text-[var(--destructive)]";
    case "stopped":
      return "border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-[var(--wallet-ops-muted)]";
    default:
      return "border-[var(--warning)]/40 bg-[var(--warning)]/10 text-[var(--warning)]";
  }
}

function priorityClass(priority: CRMRecommendationPriority) {
  switch (priority) {
    case "high":
      return "border-[var(--destructive)]/40 bg-[var(--destructive)]/10 text-[var(--destructive)]";
    case "medium":
      return "border-[var(--warning)]/40 bg-[var(--warning)]/10 text-[var(--warning)]";
    default:
      return "border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-[var(--wallet-ops-muted)]";
  }
}

function formatNumber(value: number) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatCurrency(value: number) {
  return Number(value || 0).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function formatStorage(resource: CRMResource) {
  const parts = [];
  if (resource.volumeGb != null && resource.volumeGb > 0) parts.push(`${formatNumber(resource.volumeGb)} GB volume`);
  if (resource.containerDiskGb != null && resource.containerDiskGb > 0) parts.push(`${formatNumber(resource.containerDiskGb)} GB disk`);
  return parts.length > 0 ? parts.join(" · ") : "pending";
}

function formatUtilization(resource: CRMResource) {
  const parts = [];
  if (resource.gpuUtilizationPercent != null) parts.push(`GPU ${formatPercent(resource.gpuUtilizationPercent)}`);
  if (resource.cpuUtilizationPercent != null) parts.push(`CPU ${formatPercent(resource.cpuUtilizationPercent)}`);
  if (resource.memoryUtilizationPercent != null) parts.push(`MEM ${formatPercent(resource.memoryUtilizationPercent)}`);
  if (resource.diskUtilizationPercent != null) parts.push(`DISK ${formatPercent(resource.diskUtilizationPercent)}`);
  return parts.length > 0 ? parts.join(" · ") : "pending";
}

function formatWorkloadUtilization(workload: CRMWorkload) {
  const parts = [];
  if (workload.gpuUtilizationPercent != null) parts.push(`GPU ${formatPercent(workload.gpuUtilizationPercent)}`);
  if (workload.cpuUtilizationPercent != null) parts.push(`CPU ${formatPercent(workload.cpuUtilizationPercent)}`);
  return parts.length > 0 ? parts.join(" · ") : "pending";
}

function formatPercent(value: number) {
  return `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatDuration(seconds: number) {
  if (!seconds) return "pending";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours <= 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}
